import { Button } from "antd";
import { useEffect, useState } from "react";

import { operationHistoryService } from "@/api/services/operationHistoryService";
import type { ChangeInfo, OperationHistory } from "@/entity/logistics/operationHistory";

export interface OperationHistoryPanelProps {
    /** 业务详情主键，统一转换为接口要求的字符串。 */
    bizNo: string | number;
    /** 旧 Vue Operation_History_Enum 中的业务模块编号。 */
    module: number;
    /** 合同详情沿用旧 Vue 的历史记录空态文案。 */
    emptyText?: string;
    /** 嵌入详情大区块时隐藏面板外框，沿用旧 Vue DetailJson 的区块标题。 */
    hideFrame?: boolean;
    /** 区块标题，独立面板默认显示“操作日志”。 */
    title?: string;
    /** 业务操作成功后递增此值，触发日志重新查询。 */
    refreshToken?: number;
}

type OperationHistoryRecord = OperationHistory & {
    operationContent?: ChangeInfo[] | ChangeInfo;
};

function displayDateTime(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : "--";
}

function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, character => {
        const entities: Record<string, string> = {
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;"
        };
        return entities[character];
    });
}

/** 旧 Vue 会从 operationAction 中拆出时间，并将 disposes 命中的动作加粗。 */
function formatOperationAction(log: OperationHistoryRecord): { time: string; html: string } {
    const rawAction = log.operationAction?.trim() || "--";
    const timeMatch = rawAction.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/);
    const time = timeMatch?.[0] || displayDateTime(log.createdTime);
    const plainAction = (timeMatch ? rawAction.replace(timeMatch[0], "") : rawAction).trim() || "--";
    const action = /[。.!！?？]$/.test(plainAction) ? plainAction : `${plainAction}。`;
    const disposeWords = (log.disposes || "")
        .split(",")
        .map(item => item.trim())
        .filter(Boolean)
        .sort((left, right) => right.length - left.length);

    let html = escapeHtml(action);
    for (const disposeWord of disposeWords) {
        const escapedWord = escapeHtml(disposeWord);
        html = html.split(escapedWord).join(`<strong class="operation-history-highlight">${escapedWord}</strong>`);
    }
    return { time, html };
}

function displayChangeValue(value: unknown): string {
    return value == null || value === "" ? "--" : String(value);
}

function normalizeChanges(log: OperationHistoryRecord): ChangeInfo[] {
    if (Array.isArray(log.operationContent)) return log.operationContent;
    return log.operationContent ? [log.operationContent] : [];
}

/** 展示旧 Vue 操作日志的时间、动作及可展开字段变更。 */
export function OperationHistoryPanel({
    bizNo,
    module,
    emptyText = "暂无操作日志",
    hideFrame = false,
    title = "操作日志",
    refreshToken = 0
}: OperationHistoryPanelProps) {
    const [logs, setLogs] = useState<OperationHistoryRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    useEffect(() => {
        let disposed = false;
        setLoading(true);
        setExpanded({});

        void operationHistoryService
            .getDetail({ bizNo: String(bizNo), module })
            .then(result => {
                if (!disposed) setLogs(Array.isArray(result) ? (result as OperationHistoryRecord[]) : []);
            })
            .catch(() => {
                // 日志接口失败不应阻塞详情主体，按旧页面空态继续展示。
                if (!disposed) setLogs([]);
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [bizNo, module, refreshToken]);

    // 独立操作日志沿用旧 Vue：无日志时不额外生成空白卡片，避免详情页高度发生变化。
    if (!hideFrame && (loading || !logs.length)) return null;

    const content = loading ? (
        <div aria-label="操作日志加载中" className="operation-history-empty" role="status">
            加载中...
        </div>
    ) : logs.length ? (
        <ol aria-label="操作日志列表" className="operation-history-list">
            {logs.map((log, index) => {
                const key = String(log.id ?? `${log.createdTime}-${index}`);
                const changes = normalizeChanges(log);
                const isExpanded = Boolean(expanded[key]);
                const operation = formatOperationAction(log);

                return (
                    <li key={key} className="operation-history-item">
                        <div className="operation-history-line">
                            <span className="operation-history-index">{index + 1}.</span>
                            <span className="operation-history-time">{operation.time}</span>
                            <span
                                className="operation-history-content"
                                dangerouslySetInnerHTML={{ __html: operation.html }}
                            />
                            {changes.length ? (
                                <Button
                                    aria-label={isExpanded ? "收起日志详情" : "展开日志详情"}
                                    size="small"
                                    type="link"
                                    onClick={() => setExpanded(current => ({ ...current, [key]: !isExpanded }))}
                                >
                                    <i
                                        aria-hidden="true"
                                        className={`iconfont ${isExpanded ? "icon-up" : "icon-down"}`}
                                    />
                                </Button>
                            ) : null}
                        </div>
                        {isExpanded ? (
                            <div className="operation-history-changes">
                                {changes.map((change, changeIndex) => (
                                    <div key={`${key}-${changeIndex}`}>
                                        修改了 <strong>{displayChangeValue(change.key)}，</strong>旧值为“
                                        {displayChangeValue(change.oldValue)}”，新值为“
                                        {displayChangeValue(change.newValue)}”。
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </li>
                );
            })}
        </ol>
    ) : (
        <div role="status" className="operation-history-empty">
            {emptyText}
        </div>
    );

    if (hideFrame) {
        return (
            <div aria-label={title} className="operation-history-panel operation-history-panel--embedded">
                <h2 className="section-title">{title}</h2>
                {content}
            </div>
        );
    }

    return (
        <section aria-label={title} className="section-card mt-4 operation-history-panel">
            <h2 className="section-title">{title}</h2>
            {content}
        </section>
    );
}

export default OperationHistoryPanel;
